import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { MaterialModule } from '../material.module';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import {
  CalibrationRecord,
  CalibrationRecordValue,
  CalibrationValueRequest,
  OutOfSpecificationResponse,
  UpdateCalibrationRecordRequest,
} from './lab-equipment.models';
import { LabEquipmentService } from './lab-equipment.service';

export interface CalibrationRecordEditDialogData {
  record: CalibrationRecord;
}

@Component({
  selector: 'app-calibration-record-edit-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './calibration-record-edit-dialog.component.html',
  styleUrl: './calibration-record-edit-dialog.component.scss',
})
export class CalibrationRecordEditDialogComponent {
  private readonly service = inject(LabEquipmentService);
  private readonly dialog = inject(MatDialog);
  readonly dialogRef = inject(MatDialogRef<CalibrationRecordEditDialogComponent>);
  readonly data = inject<CalibrationRecordEditDialogData>(MAT_DIALOG_DATA);
  readonly values = new FormGroup<Record<string, FormControl<string | number | boolean | null>>>({});
  readonly form = new FormGroup({
    calibrationDate: new FormControl(this.data.record.calibrationDate, { nonNullable: true, validators: Validators.required }),
    notes: new FormControl(this.data.record.notes ?? '', { nonNullable: true, validators: Validators.maxLength(4000) }),
  });
  isSaving = false;
  errorMessage = '';

  constructor() {
    this.form.controls.calibrationDate.valueChanges.subscribe(date => {
      this.form.controls.notes.setValidators([
        Validators.maxLength(4000),
        ...(date !== this.data.record.calibrationDate ? [Validators.required, Validators.pattern(/\S/)] : []),
      ]);
      this.form.controls.notes.updateValueAndValidity();
    });
    for (const value of this.data.record.values) {
      this.values.addControl(
        this.fieldKey(value.calibrationTemplateFieldId),
        new FormControl(this.initialValue(value), Validators.required),
      );
    }
  }

  get periodDescription(): string {
    const record = this.data.record;
    if (record.frequency === 'Annual') return `${record.periodYear}`;
    const period = record.frequency === 'Weekly' ? 'Week' : record.frequency === 'Monthly' ? 'Month' : 'Quarter';
    return `${record.periodYear} · ${period} ${record.periodNumber}`;
  }

  fieldControl(value: CalibrationRecordValue): FormControl<string | number | boolean | null> {
    return this.values.controls[this.fieldKey(value.calibrationTemplateFieldId)];
  }

  startsNewRow(index: number): boolean {
    const values = this.data.record.values;
    const current = values[index];
    const previous = values[index - 1];
    const beforePrevious = values[index - 2];
    return current.valueType === 'Numeric'
      && previous?.valueType === 'Text'
      && (!beforePrevious || beforePrevious.valueType !== 'Text');
  }

  numericValueStatus(value: CalibrationRecordValue): 'target' | 'range' | 'outside' | null {
    if (value.valueType !== 'Numeric') return null;

    const currentValue = this.fieldControl(value).value;
    if (currentValue === null || currentValue === '') return null;

    const numericValue = Number(currentValue);
    if (!Number.isFinite(numericValue)) return null;
    if (value.targetValue !== null && numericValue === value.targetValue) return 'target';

    const outsideMinimum = value.minimumValue !== null && numericValue < value.minimumValue;
    const outsideMaximum = value.maximumValue !== null && numericValue > value.maximumValue;
    if (outsideMinimum || outsideMaximum) return 'outside';

    return value.minimumValue !== null || value.maximumValue !== null ? 'range' : null;
  }

  openDatePicker(input: HTMLInputElement): void {
    input.showPicker?.();
  }

  save(confirmOutOfSpecification = false): void {
    if (this.form.invalid || this.values.invalid || this.isSaving) {
      this.form.markAllAsTouched();
      this.values.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();
    const request: UpdateCalibrationRecordRequest = {
      calibrationDate: formValue.calibrationDate,
      notes: formValue.notes.trim() || null,
      confirmOutOfSpecification,
      values: this.data.record.values.map(value => this.toRequestValue(value)),
    };

    this.isSaving = true;
    this.errorMessage = '';
    this.service.updateCalibrationRecord(this.data.record.id, request)
      .pipe(finalize(() => this.isSaving = false))
      .subscribe({
        next: record => this.dialogRef.close(record),
        error: error => {
          if (error?.status === 422) {
            const response = error.error as OutOfSpecificationResponse;
            this.dialog.open(ConfirmDialogComponent, {
              width: '520px',
              maxWidth: 'calc(100vw - 32px)',
              data: {
                title: 'Values Outside Range',
                message: `${response.message} Review the readings below before saving these changes.`,
                confirmLabel: 'Save Anyway',
                cancelLabel: 'Review Values',
                variant: 'warning',
                details: response.warnings.map(warning => ({
                  label: warning.label,
                  value: this.warningComparison(warning),
                })),
              },
            }).afterClosed().subscribe(confirmed => {
              if (confirmed) this.save(true);
            });
            return;
          }

          this.errorMessage = this.describeError(error);
        },
      });
  }

  private initialValue(value: CalibrationRecordValue): string | number | boolean | null {
    return value.numericValue ?? value.booleanValue ?? value.textValue;
  }

  private toRequestValue(value: CalibrationRecordValue): CalibrationValueRequest {
    const measured = this.fieldControl(value).value;
    if (value.valueType === 'Numeric') {
      return { calibrationTemplateFieldId: value.calibrationTemplateFieldId, numericValue: Number(measured) };
    }
    if (value.valueType === 'Boolean') {
      return { calibrationTemplateFieldId: value.calibrationTemplateFieldId, booleanValue: measured as boolean };
    }
    return { calibrationTemplateFieldId: value.calibrationTemplateFieldId, textValue: String(measured) };
  }

  private warningComparison(warning: OutOfSpecificationResponse['warnings'][number]): string {
    const unit = warning.unit ? ` ${warning.unit}` : '';
    let expected = 'specified range';
    if (warning.minimumValue !== null && warning.maximumValue !== null) {
      expected = `${warning.minimumValue}-${warning.maximumValue}${unit}`;
    } else if (warning.minimumValue !== null) {
      expected = `at least ${warning.minimumValue}${unit}`;
    } else if (warning.maximumValue !== null) {
      expected = `at most ${warning.maximumValue}${unit}`;
    }
    return `Measured ${warning.value}${unit} · Expected ${expected}`;
  }

  private describeError(error: unknown): string {
    const apiError = error as { error?: { detail?: string; errors?: Record<string, string[]> } };
    if (apiError.error?.detail) return apiError.error.detail;
    if (apiError.error?.errors) return Object.values(apiError.error.errors).flat().join(' ');
    return 'The calibration record could not be updated. Please try again.';
  }

  private fieldKey(id: number): string {
    return `field_${id}`;
  }
}